/*** IMPORTS FROM imports-loader ***/
var fetchPolyfill = require("web/script/polyfills/fetch");
var classListPolyfill = require("web/script/polyfills/classList");

import analytics from "web/script/analytics/analytics";
import baustein from "baustein";
import globals from "web/script/modules/globals";
import rothko from "web/script/modules/rothko";
import { addVisibilityWatcher } from "web/script/modules/run-loop";
import template from "templates/modules/filter_previews/filter_previews";
import url from "web/script/utils/url";
import browser from "web/script/utils/browser";

const componentName = "filter-previews";
const buttonSelectorPrefix = `.${componentName}__scroll-button`;
const previousButtonSelector = `${buttonSelectorPrefix}__previous`;
const nextButtonSelector = `${buttonSelectorPrefix}__next`;
const scrollContainerSelector = `.${componentName}__wrapper`;
const innerScrollContainerSelector = `.${componentName}__inner-wrapper`;
const dotSelector = `.${componentName}__dot`;
const activeDotClassName = `${componentName}__dot--active`;
const activeDotSelector = `${dotSelector}--active`;

const buttonDisabledClassName = `${componentName}__scroll-button--disabled`;
const activePreviousButtonSelector = `${previousButtonSelector}:not(.${buttonDisabledClassName})`;
const activeNextButtonSelector = `${nextButtonSelector}:not(.${buttonDisabledClassName})`;

const cardWidth = 222 + 20;
const scrollAmount = cardWidth * 2;

export default baustein.register("filter-previews", {
    template,

    init: function() {
        this._removeVisiblityWatcher = null;
        this._isAnimating = false;
    },

    setupEvents(add) {
        add("click", activePreviousButtonSelector, this.onPreviousClick);
        add("click", activeNextButtonSelector, this.onNextClick);
        add("scroll", scrollContainerSelector, this._onScroll);
    },

    onInsert() {
        this._removeVisiblityWatcher = addVisibilityWatcher(this.el, visible => {
            if (visible) {
                if (this.options.lazyLoad) {
                    this.fetchAndRender();
                } else {
                    // if it's not lazy loaded, send seen event
                    analytics.event(`${this.options.event.previews_name}`, "Seen", null, true);
                }
                this._removeVisiblityWatcher();
            }
        });
    },

    fetchAndRender() {
        const parsedUrl = url.parse(globals.window.location.href);
        const cards = Array.from(this.el.getElementsByClassName("filter-previews-card--js"));
        const cardData = cards.map(card => card.dataset);
        const cardParamSets = cardData.map(data => {
            let queryParams = new url.URLSearchParams(data.filters);
            // use given ref if provided, otherwise use current path
            if (!queryParams.get("ref")) {
                queryParams.set("ref", parsedUrl.path);
            }
            return queryParams;
        });

        const promises = cardParamSets.map(cardParams =>
            rothko.fetch("modules/filter_preview_products", cardParams)
        );
        Promise.all(promises)
            .then(cardResults => {
                // once products from all cards are loaded, inject the images and product counts
                const cardsWithProducts = cardData.map((data, idx) => {
                    // will need this step to convert DOMStringMap to Object first
                    // fails on Safari otherwise
                    const card = JSON.parse(JSON.stringify(data));
                    return Object.assign({}, card, cardResults[idx]);
                });
                // we want to hide cards with no products
                const filteredCardsWithProducts = cardsWithProducts.filter(card => {
                    return card.products.length > 0;
                });
                this.setRenderContext({
                    filter_previews: {
                        cards: filteredCardsWithProducts,
                        type: this.options.previewsType,
                        title: this.options.previewsTitle,
                        event: this.options.event,
                        is_small_cards: this.options.smallCards
                    },
                    highlight: this.options.highlight
                });
                analytics.event(`${this.options.event.previews_name}`, "Seen", null, true);
            })
            .catch(err => {
                console.error(`Error fetching ${this.options.event.previews_name} card:`, err);
                this.destroy();
                analytics.event(
                    `${this.options.event.previews_name}`,
                    "Failed to load",
                    null,
                    true
                );
            });
    },

    onPreviousClick() {
        this._scrollBy(-scrollAmount);
    },

    onNextClick() {
        this._scrollBy(scrollAmount);
    },

    _scrollBy(amount) {
        // ignore button scroll if it's in the middle of animation
        if (!this._isAnimating) {
            const scrollContainer = this.el.querySelector(scrollContainerSelector);
            browser.animate(scrollContainer, "scrollLeft", amount, 500).then(() => {
                this._isAnimating = false;
            });
            this._toggleScrollButtonCssClass();
            this._updateNavigationDots();

            const scrollDirection = amount < 0 ? "previous" : "next";
            analytics.event(
                `${this.options.event.previews_name}`,
                `Clicked ${scrollDirection} scroll button`
            );
        }
    },

    /**
     * toggle scroll button css class depending on whether it's
     * at the beginning of scroll, middle of scroll, or end of scroll
     */
    _toggleScrollButtonCssClass() {
        const scrollContainer = this.el.querySelector(scrollContainerSelector);
        const totalCardsWidth = scrollContainer.querySelector(innerScrollContainerSelector)
            .offsetWidth;
        const nextButton = this.el.querySelector(nextButtonSelector);
        const previousButton = this.el.querySelector(previousButtonSelector);

        const isBeginningOfScroll = scrollContainer.scrollLeft === 0;
        const isEndOfScroll =
            Math.ceil(scrollContainer.scrollLeft + scrollContainer.offsetWidth) === totalCardsWidth;

        if (isBeginningOfScroll) {
            previousButton.classList.add(buttonDisabledClassName);
            nextButton.classList.remove(buttonDisabledClassName);
        } else if (isEndOfScroll) {
            previousButton.classList.remove(buttonDisabledClassName);
            nextButton.classList.add(buttonDisabledClassName);
        } else {
            previousButton.classList.remove(buttonDisabledClassName);
            nextButton.classList.remove(buttonDisabledClassName);
        }
    },

    _updateNavigationDots() {
        const innerScrollContainer = this.el.querySelector(innerScrollContainerSelector);
        const scrollContainer = this.el.querySelector(scrollContainerSelector);
        const dots = Array.from(this.el.querySelectorAll(dotSelector));
        const dotsCount = dots.length;
        const currentActiveDot = this.el.querySelector(activeDotSelector);

        const { left, right } = innerScrollContainer.getBoundingClientRect();
        const viewportWidth = globals.window.innerWidth;

        const totalScrollableDistance = right - left - viewportWidth;
        const scrollDistancePerDot = totalScrollableDistance / dotsCount;

        let nextActiveDotIndex = Math.floor(scrollContainer.scrollLeft / scrollDistancePerDot);

        // handling the case for when the container is moved beyond existing dot indices
        if (nextActiveDotIndex < 0) {
            nextActiveDotIndex = 0;
        } else if (nextActiveDotIndex > dotsCount - 1) {
            nextActiveDotIndex = dotsCount - 1;
        }

        if (currentActiveDot === nextActiveDotIndex) {
            return;
        }

        dots.forEach((element, index) => {
            if (index !== nextActiveDotIndex) {
                element.classList.remove(activeDotClassName);
            } else {
                element.classList.add(activeDotClassName);
            }
        });
    },

    _onScroll() {
        this._toggleScrollButtonCssClass();
        this._updateNavigationDots();
    }
});

